import { useMemo, useState, type ChangeEvent } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { FileCategory } from '../../types/file';
import { fileService } from '../../services/fileService';
import { useNotificationStore } from '../../store/notificationStore';

export interface FileUploadProps {
  sessionId: string;
  onUploaded?: () => void;
}

type UploadItem = {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
};

const categoryLabel = (c: FileCategory) => {
  switch (c) {
    case 0:
      return 'Input';
    case 1:
      return 'Output';
    case 2:
      return 'Generated';
    case 3:
      return 'Attachment';
    case 4:
      return 'Temporary';
    case 5:
      return 'Artifact';
    case 6:
      return 'Log';
    default:
      return 'Unknown';
  }
};

/**
 * Upload component for session files with drag-and-drop and progress.
 */
export const FileUpload = ({ sessionId, onUploaded }: FileUploadProps) => {
  const { addNotification } = useNotificationStore();

  const [category, setCategory] = useState<FileCategory>(0);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [uploading, setUploading] = useState(false);

  const canUpload = useMemo(() => items.some((i) => i.status === 'pending') && !uploading, [items, uploading]);

  const enqueueFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setItems((prev) => [
      ...prev,
      ...arr.map((f) => ({
        file: f,
        progress: 0,
        status: 'pending' as const,
      })),
    ]);
  };

  const handleSelectFiles = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    enqueueFiles(e.target.files);
    // Allow selecting same file again
    e.target.value = '';
  };

  const startUpload = async () => {
    setUploading(true);
    try {
      for (let idx = 0; idx < items.length; idx++) {
        const item = items[idx];
        if (item.status !== 'pending') continue;

        setItems((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], status: 'uploading', progress: 0, error: undefined };
          return next;
        });

        try {
          await fileService.uploadFile(sessionId, item.file, category, {
            onProgress: (p) => {
              setItems((prev) => {
                const next = [...prev];
                if (!next[idx]) return prev;
                next[idx] = { ...next[idx], progress: p };
                return next;
              });
            },
          });

          setItems((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], status: 'done', progress: 100 };
            return next;
          });

          onUploaded?.();
        } catch (err: any) {
          const msg = err.response?.data?.message || err.message || 'Upload failed';
          setItems((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], status: 'error', error: msg };
            return next;
          });
          addNotification(msg, 'error');
        }
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Files
      </Typography>

      <FormControl size="small" fullWidth sx={{ mb: 1 }}>
        <InputLabel id="file-category-label">Category</InputLabel>
        <Select
          labelId="file-category-label"
          value={category}
          label="Category"
          onChange={(e) => setCategory(e.target.value as FileCategory)}
          disabled={uploading}
        >
          {[0, 1, 2, 3, 4, 5, 6].map((c) => (
            <MenuItem key={c} value={c}>
              {categoryLabel(c as FileCategory)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          textAlign: 'center',
          bgcolor: 'background.paper',
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          enqueueFiles(e.dataTransfer.files);
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag & drop files here, or choose files
        </Typography>

        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={uploading}>
          Choose files
          <input type="file" hidden multiple onChange={handleSelectFiles} />
        </Button>

        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={startUpload} disabled={!canUpload}>
            Upload
          </Button>
        </Box>
      </Box>

      {items.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {items.slice(0, 5).map((it, i) => (
            <Box key={`${it.file.name}-${i}`} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {it.file.name} ({Math.round(it.file.size / 1024)} KB) — {it.status}
                {it.status === 'error' && it.error ? `: ${it.error}` : ''}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={it.progress}
                sx={{ height: 6, borderRadius: 1 }}
                color={it.status === 'error' ? 'error' : 'primary'}
              />
            </Box>
          ))}
          {items.length > 5 && (
            <Typography variant="caption" color="text.secondary">
              Showing 5 of {items.length} files.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

